import { computed, watch, ref } from '@vue/composition-api'
import store from '@/store'


export default function useAppConfig() {
  // ------------------------------------------------
  // isVerticalMenuCollapsed
  // ------------------------------------------------
  const isVerticalMenuCollapsed = computed({
    get: () => store.state.verticalMenu.isVerticalMenuCollapsed,
    set: val => {
      store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', val)
    },
  })

  // ------------------------------------------------
  // RTL
  // ------------------------------------------------
  const isRTL = computed({
    get: () => store.state.appConfig.layout.isRTL,
    set: val => {
      store.commit('appConfig/TOGGLE_RTL', val)
    },
  })

  // ------------------------------------------------
  // Skin
  // ------------------------------------------------
  const skin = computed({
    get: () => store.state.appConfig.layout.skin,
    set: val => {
      store.commit('appConfig/UPDATE_SKIN', val)
    },
  })

  const skinClasses = computed(() => {
    if (skin.value === 'bordered') return 'bordered-layout'
    if (skin.value === 'semi-dark') return 'semi-dark-layout'

    // Do not return any class for dark layout because dark layout updates class in body
    // Do not return any class for light layout as that is default layout
    return null
  })

  // ------------------------------------------------
  // routerTransition
  // ------------------------------------------------
  const routerTransition = computed({
    get: () => store.state.appConfig.layout.routerTransition,
    set: val => {
      store.commit('appConfig/UPDATE_ROUTER_TRANSITION', val)
    },
  })

  // *===============================================---*
  // *--------- LAYOUT ---------------------------------------*
  // *===============================================---*

  // ------------------------------------------------
  // layoutType
  // ------------------------------------------------

  const layoutType = computed({
    get: () => store.state.appConfig.layout.type,
    set: val => {
      store.commit('appConfig/UPDATE_LAYOUT_TYPE', val)
    },
  })

  // Reset skin if skin is semi-dark and move to horizontal layout
  watch(layoutType, val => {
    if (val === 'horizontal' && skin.value === 'semi-dark') skin.value = 'light'
  })

  // ------------------------------------------------
  // Content Width (Full/Boxed)
  // ------------------------------------------------
  const contentWidth = computed({
    get: () => store.state.appConfig.layout.contentWidth,
    set: val => {
      store.commit('appConfig/UPDATE_CONTENT_WIDTH', val)
    },
  })

  // ------------------------------------------------
  // isNavMenuHidden
  // ------------------------------------------------
  const isNavMenuHidden = computed({
    get: () => store.state.appConfig.layout.menu.hidden,
    set: val => {
      store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', val)
    },
  })

  // *===============================================---*
  // *--------- NAVBAR ---------------------------------------*
  // *===============================================---*

  const navbarBackgroundColor = computed({
    get: () => store.state.appConfig.layout.navbar.backgroundColor,
    set: val => {
      store.commit('appConfig/UPDATE_NAVBAR_CONFIG', { backgroundColor: val })
    },
  })

  const navbarType = computed({
    get: () => store.state.appConfig.layout.navbar.type,
    set: val => {
      store.commit('appConfig/UPDATE_NAVBAR_CONFIG', { type: val })
    },
  })

  // *===============================================---*
  // *--------- FOOTER ---------------------------------------*
  // *===============================================---*

  const footerType = computed({
    get: () => store.state.appConfig.layout.footer.type,
    set: val => {
      store.commit('appConfig/UPDATE_FOOTER_CONFIG', { type: val })
    },
  })

  // *===============================================---*
  // *--------- _USERDATA ---------------------------------------*
  // *===============================================---*
  const userData = computed({
    get: () => {
      if (process.env.VUE_APP_AUTH == "CL") return store.state.appConfig.user.userData
      return JSON.parse(localStorage.getItem('userData'))
    },
    set: val => {
      store.commit('appConfig/UPDATE_USER', val)
    },
  })
  // *===============================================---*
  // *--------- APP DATA--------------------------------*
  // *===============================================---*
  // const AppName = ref(store.state.appConfig.app.AppName)
  const fetchAppData = async () => {
    await store
      .dispatch('appConfig/fetchCompanyConfigs')
      .then(response => {
        // AppName.value(response.data.CompnyName)
        store.commit('appConfig/UPDATE_APPNAME', response.data.company.CompnyName)
        store.commit('appConfig/UPDATE_APPLOGO', response.data.company.Logo)
        store.commit('appConfig/UPDATE_APPMODULES', response.data.modules.modules)
        store.commit('appConfig/UPDATE_APPBASEPATH', response.data.company.DefaultBasePath)
        localStorage.setItem('APPBASEPATH', response.data.company.DefaultBasePath)
      })
  }

  const fetchPortalConfigs = async () => {
    await store
      .dispatch('appConfig/fetchPortalConfigs')
      .then(response => {
        // AppName.value(response.data.CompnyName)
        store.commit('appConfig/UPDATE_APPCONFIG', response.data.Data)
      })
  }

  const BasePath = computed({
    get: () => store.state.appConfig.app.BasePath,
    set: val => {
      store.commit('appConfig/UPDATE_APPBASEPATH', { BasePath: val })
    },
  })

  const AppName = computed({
    get: () => store.state.appConfig.app.AppName,
    set: val => {
      store.commit('appConfig/UPDATE_APPLOGO', { AppName: val })
    },
  })

  const AppLogo = computed({
    get: () => store.state.appConfig.app.Logo,
    set: val => {
      store.commit('appConfig/UPDATE_APPLOGO', { Logo: val })
    },
  })

  const AppModules = computed({
    get: () => store.state.appConfig.app.Modules,
    set: val => {
      store.commit('appConfig/UPDATE_APPMODULES', { Modules: val })
    },
  })

  const AppConfig = computed({
    get: () => store.state.appConfig.app.configs,
    set: obj => {
      store.commit('appConfig/UPDATE_APPCONFIG', { configs: obj })
    },
  })

  return {
    isVerticalMenuCollapsed,
    isRTL,
    skin,
    skinClasses,
    routerTransition,

    // Navbar
    navbarBackgroundColor,
    navbarType,

    // Footer
    footerType,

    // Layout
    layoutType,
    contentWidth,
    isNavMenuHidden,

    // App
    AppLogo,
    AppName,
    AppModules,
    BasePath,
    AppConfig,

    fetchAppData,
    fetchPortalConfigs,

    userData,
  }
}
